<template>
  <div class="v2026-composer" v-if="selectedConversation">
    <!-- version2026: the composer's own top border, made draggable. Absolute, so it adds no layout height. -->
    <div class="v2026-composer__resizer"
         :class="{ 'is-resizing': !!composerResizing }"
         role="separator" aria-orientation="horizontal"
         aria-label="ปรับความสูงกล่องพิมพ์ข้อความ" title="ลากเพื่อปรับความสูง (ดับเบิลคลิกเพื่อรีเซ็ต)"
         @pointerdown="onComposerResizeStart($event)"
         @pointermove="onComposerResizeMove"
         @pointerup="onComposerResizeEnd"
         @pointercancel="onComposerResizeEnd"
         @dblclick="resetComposerDragHeight"></div>
    <textarea
      rows="2"
      ref="message"
      class="v2026-composer__input"
      id="inlineFormInputGroup"
      placeholder="พิมพ์ข้อความ หรือใช้คำสั่งลัด"
      v-model="message"
      @keydown.shift.enter="sendMessage"
    />
    <!-- version2026: per-platform text limit. Deliberately NOT routed through .v2026-composer__hint —
         that slot is TTL-cleared by setComposerHint() and must not carry persistent state. No DOM
         node for an empty draft, and none at any length when the platform has no limit. -->
    <div v-if="showLimitMeter" class="v2026-limit" :class="'v2026-limit--' + limitState">
      <span v-if="isOverTextLimit" :id="warningLineId" class="v2026-limit__warn"
            role="status" aria-live="polite">{{ warningText }}</span>
      <span class="v2026-limit__count" aria-hidden="true">
        <svg v-if="isOverTextLimit" class="v2026-limit__glyph" viewBox="0 0 24 24" width="14" height="14"
             aria-hidden="true">
          <circle cx="12" cy="12" r="12" fill="currentColor"/>
          <path d="M12 6.25v7" fill="none" stroke="#ffffff" stroke-width="2" stroke-linecap="round"/>
          <circle cx="12" cy="17.25" r="1.35" fill="#ffffff"/>
        </svg>
        <span class="v2026-limit__platform">{{ platformLabel }}</span>
        <span class="v2026-limit__nums">{{ counterText }}</span>
      </span>
      <!-- created with the meter, so for ordinary typing it is long-established by 80%. The watcher's
           $nextTick covers the one case that creates and fills it in a single render: a draft jumping
           from empty straight past 80% (paste, template, applyComposerInsert, AI reply). -->
      <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ limitAnnouncement }}</span>
    </div>
    <!-- version2026: append notice. In flow, never absolutely positioned — it pushes the bar down, it does not cover it. -->
    <div v-if="composerHint" class="v2026-composer__hint" role="status" aria-live="polite">{{ composerHint }}</div>
    <template>
      <div class="text-right" v-for="admin in typingAdmins" :key="admin.id">
        <small class="text-muted">{{ `${admin.name} กำลังพิมพ์...` }}</small>
      </div>
    </template>

    <hr class="v2026-composer__divider"/>

    <div class="v2026-composer__bar">
      <div class="v2026-composer__left">
        <div v-if="fileUploads && fileUploads.length === 1" class="v2026-file-badge">
          <span
            class="badge badge-secondary"
            v-for="(file, index) in fileUploads"
            :key="index"
          >
            {{ file.name | shortText(50) }}
            <span role="button" @click="removeFileUpload(index)">
              <i class="fa fa-times"></i>
            </span>
          </span>
        </div>
        <template v-else>
          <label class="v2026-icon-btn mb-0" aria-label="แนบรูปภาพ">
            <span class="v2026-icon-btn__svg" aria-hidden="true"
                  :style="{ '-webkit-mask-image': 'url(' + composerIcons.camera + ')', 'mask-image': 'url(' + composerIcons.camera + ')' }"></span>
            <input
              type="file"
              multiple
              ref="imageFiles"
              @change="handleFileUpload('image')"
              hidden
            />
          </label>
          <label class="v2026-icon-btn mb-0" aria-label="แนบไฟล์">
            <span class="v2026-icon-btn__svg" aria-hidden="true"
                  :style="{ '-webkit-mask-image': 'url(' + composerIcons.attach + ')', 'mask-image': 'url(' + composerIcons.attach + ')' }"></span>
            <input
              type="file"
              multiple
              ref="pdfFiles"
              @change="handleFileUpload('pdf')"
              hidden
            />
          </label>
          <label class="v2026-icon-btn mb-0" aria-label="แนบวิดีโอ">
            <span class="v2026-icon-btn__svg" aria-hidden="true"
                  :style="{ '-webkit-mask-image': 'url(' + composerIcons.video + ')', 'mask-image': 'url(' + composerIcons.video + ')' }"></span>
            <input
              type="file"
              multiple
              ref="mp4Files"
              @change="handleFileUpload('mp4')"
              hidden
            />
          </label>

          <!-- Rollback branch. Both carry ref="dropdown", so selectMessageTemplate's
               hide(true) never learns which one is live. -->
          <b-dropdown
            v-if="!useV2026MessageTemplateMenu"
            id="dropdown-dropup"
            dropup
            variant="link"
            toggle-class="v2026-keylad"
            no-caret
            ref="dropdown">
            <template v-slot:button-content>
              คีย์ลัด
              <i class="fas fa-chevron-up v2026-chev"></i>
            </template>
            <b-card no-body>
              <b-card-header header-tag="nav">
                <div class="row">
                  <div class="col align-self-center">
                    <input type="search" class="form-control" @input="debounceSearchKeyword($event.target.value)" placeholder="ค้นหาคำ">
                  </div>
                  <div class="col-3 text-right align-self-center">
                    <router-link to="/settings/message-template">ตั้งค่า
                    </router-link>
                  </div>
                </div>
              </b-card-header>
              <b-card-body
                class="message-template-area text-small p-0"
                @scroll.passive="handleScroll"
              >
                <div
                  class="border-bottom"
                  v-for="(messageCategory, index) in messageTemplates[`${normalizedCustomerType}`]"
                  :key="index + '_' + messageCategory.id">
                  <template v-if="messageCategory.message_template && messageCategory.message_template.length > 0">
                    <b-list-group-item variant="light" class="p-1 font-weight-bold text-dark">
                      {{ messageCategory.name }}
                    </b-list-group-item>
                    <div @click="selectMessageTemplate(messageTemplate)" class="row-message-template"
                         v-for="(messageTemplate, key) in messageCategory.message_template" :key="key + '_' + messageTemplate.id">
                      <div class="ml-2 pt-2" v-if="messageTemplate.topic">
                        <span>{{ messageTemplate.topic }}</span>
                      </div>
                      <div class="d-flex justify-content-start align-items-center  p-2 border-bottom">
                        <div class="mr-2">
                          <h5>
                            <span class="badge badge-secondary">/{{ messageTemplate.key }}</span>
                          </h5>
                        </div>
                        <div class="flex-grow-1 text-small">
                          <div v-if="messageTemplate.type === 'text'">
                              <span v-if="messageTemplate.message">
                                {{ messageTemplate.message | shortText(70) }}
                              </span>
                            <span v-else>&nbsp;</span>
                          </div>
                          <div v-else-if="messageTemplate.type === 'image'" style="overflow-x: auto; width:100vh;">
                            <span class="d-inline-block pr-2" v-for="(image, index) in messageTemplate.images" :key="index">
                              <img :src="image.url" alt="" class="rounded" width="" height="50">
                            </span>
                          </div>
                        </div>
                        <div class="float-right" v-if="messageTemplate.attachment">
                          <template v-if="getFileExtension(messageTemplate.attachment) === 'mp4'">
                            <div style="height: 80px;">
                              <vue-plyr :options="{clickToPlay: false, controls: []}">
                                <video
                                  :src="messageTemplate.attachment"
                                  format="audio/mp4"
                                />
                              </vue-plyr>
                            </div>
                          </template>
                          <template v-else>
                            <img
                              v-lazy="messageTemplate.attachment"
                              alt=""
                              class="rounded"
                              width="60"/>
                          </template>
                        </div>
                      </div>
                    </div>
                  </template>

                </div>
              </b-card-body>
            </b-card>
          </b-dropdown>

          <MessageTemplateMenuV2026 v-else
                                    ref="dropdown"
                                    :categories="messageTemplates[`${normalizedCustomerType}`]"
                                    :conversationId="selectedConversation.id"
                                    @select="selectMessageTemplate"
                                    @search="debounceSearchKeyword"
                                    @scroll-end="handleScroll" />

          <!-- The LINE gate stays outside the flag so both branches share it. -->
          <template v-if="selectedConversation.type === 'LineAtConversation' && groupPackageIds">
          <!-- Rollback branch; shared ref for the same reason as the คีย์ลัด pair above. -->
          <b-dropdown id="dropdown-2"
                      dropup
                      variant="link"
                      toggle-class="v2026-emoji"
                      no-caret
                      ref="dropdownStickeLine"
                      v-if="!useV2026StickerPicker">
            <template v-slot:button-content>
              <span class="v2026-icon-btn__svg" aria-hidden="true"
                    :style="{ '-webkit-mask-image': 'url(' + composerIcons.emoji + ')', 'mask-image': 'url(' + composerIcons.emoji + ')' }"></span>
              <i class="fas fa-chevron-up v2026-chev"></i>
            </template>
            <b-card class="p-0">
              <ul class="nav nav-tabs">
                <li class="nav-item" v-for="(groupPackageId, index) in groupPackageIds" :key="index">
                  <a class="nav-link"
                     data-toggle="tab"
                     href="#789"
                     @click.prevent="selectPackageId(groupPackageId.package_id)"
                     :class="{'active' : groupPackageId.package_id === selectedPackageId}">
                    <template>
                      <img :src="groupPackageId.url_img" height="50">
                    </template>
                  </a>
                </li>
              </ul>

              <b-card-body style="padding:0px; overflow-y: auto; width:450px; height:200px;">
                <div class="d-flex flex-wrap">
                  <div class="flex-item" v-for="(sticker, index) in stickersLine" :key="index" v-show="sticker.package_id === selectedPackageId">
                    <button class="btn btn-link"
                            @click.prevent="selectSticker(sticker)"
                            style="padding:8px;"
                            data-toggle="dropdown">
                      <img :src="sticker.url_img">
                    </button>
                  </div>
                </div>
              </b-card-body>
            </b-card>
          </b-dropdown>

          <StickerPickerV2026 v-else
                              ref="dropdownStickeLine"
                              :packages="groupPackageIds"
                              :stickers="stickersLine"
                              :selectedPackageId="selectedPackageId"
                              :leadIcon="composerIcons.emoji"
                              :conversationId="selectedConversation.id"
                              @select-package="selectPackageId"
                              @select="selectSticker" />
          </template>

          <button type="button" class="v2026-icon-btn"
                  :class="{ 'v2026-icon-btn--recording': isRecording }"
                  aria-label="อัดเสียง"
                  @click="checkMic">
            <span class="v2026-icon-btn__svg" aria-hidden="true"
                  :style="{ '-webkit-mask-image': 'url(' + composerIcons.mic + ')', 'mask-image': 'url(' + composerIcons.mic + ')' }"></span>
            <span v-if="isRecording" class="v2026-mic-hint">&nbsp;กำลังฟัง...</span>
          </button>

          <FeedbackMenuV2026 :selectedConversation="selectedConversation" />
        </template>
      </div>

      <div class="v2026-composer__right">
        <!-- version2026: 36px, so it is shorter than the 40px send button and adds ZERO height to the
             composer. First child, so .v2026-send stays flush to the bar's right edge and never moves. -->
        <button type="button" class="v2026-icon-btn"
                ref="draftReviewBtn"
                title="ตรวจทานข้อความแบบเต็มจอ"
                aria-label="ตรวจทานข้อความแบบเต็มจอ"
                aria-haspopup="dialog"
                :aria-expanded="showDraftReview ? 'true' : 'false'"
                @click="openDraftReview()">
          <i class="fas fa-window-maximize" aria-hidden="true"></i>
        </button>

        <!-- Keeps the remindAt contract — do NOT drop it. -->
        <div class="v2026-remind-anchor"
             v-if="['customer', 'professional'].includes(selectedConversation.customer_type)"
             @keydown.esc="onEsc"
             @focusout="onFocusOut">
          <button type="button"
                  ref="remindBtn"
                  class="v2026-icon-btn v2026-icon-btn--chev"
                  aria-label="ตั้งเวลาแจ้งเตือน"
                  aria-haspopup="dialog"
                  aria-controls="v2026-remind-card"
                  :aria-expanded="showRemindCard ? 'true' : 'false'"
                  @click="toggleRemindCard">
            <span class="v2026-icon-btn__svg" aria-hidden="true"
                  :style="maskStyle(composerIcons.reminder)"></span>
            <span class="small v2026-remind-label" style="padding:0px;">
              {{ remindAt | displayRemindDate }}
            </span>
            <i class="fas fa-chevron-down v2026-chev"></i>
          </button>

          <OverlayCard id="v2026-remind-card"
                       class="v2026-remind-pos"
                       :open="showRemindCard"
                       v-show="showRemindCard"
                       :ignore="remindBtnEl"
                       :renderKey="remindCardRenderKey"
                       role="dialog"
                       aria-labelledby="v2026-remind-title"
                       @close="closeRemindCard()">
            <template #default="rk">
              <div id="v2026-remind-title" class="v2026-remind__title" :data-rk="rk.dirty">
                <span class="v2026-field__ico v2026-remind__title-ico" aria-hidden="true"
                      :style="maskStyle(composerIcons.reminder)"></span>
                <span class="v2026-remind__title-text">เลือกเวลาแจ้งเตือน</span>
                <button v-show="selectedConversation.remind_at || remindAt"
                        type="button" class="v2026-remind__delete" @click.prevent="removeReminder">
                  <i class="far fa-trash-alt" aria-hidden="true"></i> ลบการแจ้งเตือน
                </button>
              </div>

              <form class="v2026-remind__form" @submit.prevent="submitConfirm">
                <div class="v2026-field">
                  <label class="v2026-field__label" for="shortcut">เมนูลัด</label>
                  <div class="v2026-field__control">
                    <span class="v2026-field__ico" aria-hidden="true"
                          :style="maskStyle(composerIcons.clock)"></span>
                    <span class="v2026-field__value"
                          :class="{ 'v2026-field__value--empty': selectedRemind === -1 }"
                          aria-hidden="true">{{ shortcutLabel }}</span>
                    <i class="fas fa-chevron-down v2026-field__chev" aria-hidden="true"></i>
                    <select class="v2026-field__native" id="shortcut" name="shortcut" ref="shortcutSelect" v-model="selectedRemind" @change="onShortcutChange($event)">
                    <!-- The placeholder row is part of remindShortcuts, so the list stays one loop. -->
                      <option v-for="opt in remindShortcuts" :key="opt.value" :value="opt.value">{{ opt.label }}</option>
                    </select>
                  </div>
                </div>

                <div class="v2026-field"
                     :class="{ 'is-open': openPanel === 'date', 'is-error': dateFieldError }">
                  <label class="v2026-field__label" id="v2026-remind-date-label">วันที่แจ้งเตือน</label>
                  <template v-if="useMonthCalendarInReminder">
                  <!-- The field row is a DIV, not a BUTTON: the clear "x" is a real button and
                       cannot be nested inside another one. The transparent hit button below
                       covers the row instead. -->
                    <div class="v2026-field__control">
                    <!-- FIRST in DOM: paints below the "x" and gives the tab order field -> "x". -->
                      <button type="button" ref="remindDateBtn" class="v2026-field__hit"
                              aria-haspopup="grid"
                              :aria-expanded="openPanel === 'date' ? 'true' : 'false'"
                              aria-controls="v2026-remind-date-panel"
                              aria-labelledby="v2026-remind-date-label v2026-remind-date-value"
                              :aria-invalid="dateFieldError ? 'true' : null"
                              :aria-describedby="dateFieldError ? 'v2026-remind-date-err' : null"
                              @click="togglePanel('date')"></button>
                      <span class="v2026-field__ico" aria-hidden="true"
                            :style="maskStyle(composerIcons.calendar)"></span>
                      <span id="v2026-remind-date-value" class="v2026-field__value"
                            :class="{ 'v2026-field__value--empty': !remindDate }">{{ remindDateLabel }}</span>
                    <!-- type="button" is MANDATORY — this sits inside the form and would
                         otherwise submit it. -->
                      <button v-if="remindDate" type="button" class="v2026-field__clear"
                              aria-label="ล้างวันที่" title="ล้างวันที่"
                              @click.stop="clearRemindDate">
                        <i class="fas fa-times" aria-hidden="true"></i>
                      </button>
                      <i class="fas fa-chevron-down v2026-field__chev" aria-hidden="true"></i>
                    </div>
                    <div v-show="openPanel === 'date'" id="v2026-remind-date-panel"
                         ref="remindDatePanel" class="v2026-field__panel v2026-remind-datepanel">
                      <MonthCalendarV2026 :month="remindMonth"
                                          :selectedDate="remindDateStr"
                                          :showCreateButton="false"
                                          yearMode="gregorian"
                                          @month-change="ym => (remindMonth = ym)"
                                          @select="onRemindDateSelect"/>
                    </div>
                    <div v-if="dateFieldError" id="v2026-remind-date-err"
                         class="v2026-field__error" role="alert">
                      กรุณาเลือกวันที่แจ้งเตือน
                    </div>
                  </template>
                  <div v-else class="v2026-field__control v2026-field__control--legacy">
                    <span class="v2026-field__ico" aria-hidden="true"
                          :style="maskStyle(composerIcons.calendar)"></span>
                    <datepicker :format="'dd/MM/yyyy'"
                                :inputClass="'v2026-field__value v2026-field__input'"
                                :placeholder="'dd/mm/yyyy'"
                                v-model="remindDate"
                                ref="remind_date_picker"
                                @input="changeInput"/>
                  </div>
                </div>

                <div class="v2026-field" :class="{ 'is-open': openPanel === 'time' }">
                  <label class="v2026-field__label" for="remind-time">เวลาที่แจ้งเตือน (ชั่วโมง:นาที)</label>
                  <div class="v2026-field__control">
                    <span class="v2026-field__ico" aria-hidden="true"
                          :style="maskStyle(composerIcons.clock)"></span>
                    <input id="remind-time" ref="remindTimeInput" type="text" required
                           class="v2026-field__value v2026-field__input"
                           v-model="remindTime"
                           placeholder="HH:mm (ตัวอย่าง 10:00)"
                           autocomplete="off"
                           role="combobox" aria-autocomplete="none"
                           aria-controls="v2026-remind-time-list"
                           :aria-expanded="openPanel === 'time' ? 'true' : 'false'"
                           @keydown.down.prevent="openTimeList()"
                           @keydown.esc.stop="onEsc($event)">
                    <button type="button" class="v2026-field__chevbtn"
                            :aria-label="openPanel === 'time' ? 'ปิดรายการเวลา' : 'เลือกเวลาจากรายการ'"
                            :aria-expanded="openPanel === 'time' ? 'true' : 'false'"
                            aria-controls="v2026-remind-time-list"
                            @click="togglePanel('time')">
                      <i class="fas fa-chevron-down v2026-field__chev" aria-hidden="true"></i>
                    </button>
                  </div>
                  <div v-show="openPanel === 'time'" id="v2026-remind-time-list" ref="remindTimePanel"
                       class="v2026-field__panel v2026-timelist" role="listbox" aria-label="เวลาแจ้งเตือน">
                    <template v-if="isTimeOffGrid">
                      <button type="button" role="option" aria-selected="true"
                              class="v2026-timelist__row v2026-timelist__current is-selected"
                              @click="pickTime(remindTime)">{{ remindTime }}</button>
                      <div class="v2026-timelist__sep" aria-hidden="true"></div>
                    </template>
                    <button v-for="opt in timeGridOptions" :key="opt"
                            type="button" role="option"
                            :aria-selected="opt === remindTime ? 'true' : 'false'"
                            class="v2026-timelist__row"
                            :class="{ 'is-selected': !isTimeOffGrid && opt === remindTime }"
                            @click="pickTime(opt)">{{ opt }}</button>
                  </div>
                </div>

                <div class="v2026-remind__footer">
                  <button type="button" class="v2026-remind__reset"
                          aria-label="ล้างค่า" title="ล้างค่า" @click.prevent="clearReminder">
                    <span class="v2026-field__ico" aria-hidden="true"
                          :style="maskStyle(composerIcons.refresh)"></span>
                  </button>
                  <button type="submit" class="v2026-remind__confirm">ยืนยัน</button>
                </div>
              </form>
            </template>
          </OverlayCard>
        </div>

        <button type="button" class="v2026-send"
                :disabled="isSending || isOverTextLimit"
                :title="isOverTextLimit ? blockedSendDetail : null"
                :aria-describedby="isOverTextLimit ? warningLineId : null"
                @click="sendMessage()">
          <span class="v2026-icon-btn__svg v2026-send__svg" aria-hidden="true"
                :style="{ '-webkit-mask-image': 'url(' + composerIcons.send + ')', 'mask-image': 'url(' + composerIcons.send + ')' }"></span>
          {{ isSending ? 'กำลังส่ง...' : 'ส่งข้อความ' }}
        </button>
      </div>
    </div>

    <!-- version2026: no local copy of the draft — `message` is passed in and written straight back, so
         no close path can discard text. Sending is delegated to the composer's own sendMessage(). -->
    <DraftReviewOverlayV2026 :open="showDraftReview"
                             :draft="message"
                             :isSending="isSending"
                             :attachmentName="draftAttachmentName"
                             :sendIcon="composerIcons.send"
                             :attachIcon="composerIcons.attach"
                             :isOverLimit="isOverTextLimit"
                             :platformLabel="platformLabel"
                             :textLimit="activeTextLimit"
                             @update:draft="onDraftReviewInput"
                             @send="onDraftReviewSend"
                             @close="closeDraftReview" />

    <!-- version2026: z-index 1070 clears BaseModal's 1060, so it is readable above the draft overlay -->
    <BaseToast :open="toast.open"
               :variant="toast.variant"
               :icon="toast.icon"
               :title="toast.title"
               :message="toast.message"
               :autoHideDelay="6000"
               @close="toast.open = false" />
  </div>
</template>

<script>
import { useSpeechRecognition } from '@vueuse/core'
import axios from 'axios'
import { mapActions, mapState } from 'vuex'
import Datepicker from 'vuejs-datepicker'
import OverlayCard from './OverlayCard'
import MessageTemplateMenuV2026 from './MessageTemplateMenuV2026'
import StickerPickerV2026 from './StickerPickerV2026'
import DraftReviewOverlayV2026 from './DraftReviewOverlayV2026'
import BaseToast from '@/components/coversation/BaseToast.vue'
import FeedbackMenuV2026 from '@/components/coversation/FeedbackMenuV2026.vue'
import MonthCalendarV2026 from '../calendar/MonthCalendarV2026'
import moment from 'moment'
import {
  countMessageLength,
  getTextLimit,
  getPlatformLabel,
  getLimitState,
  counterText,
  warningText,
  blockedSendDetail,
  nearLimitAnnouncement
} from '@/constants/messageLimits'

let limitUid = 0

const COMPOSER_HINT_TTL_MS = 10000
// fallback for the message list's own irreducible box (py-4 = 24 + 24, no border) — NOT a design
// reserve: at this height the conversation's content area is 0px. Measured live when the list is there
const MESSAGE_LIST_FLOOR = 48
// px of conversation auto-grow leaves behind; only the manual drag spends the rest
const MESSAGE_LIST_AUTO_RESERVE = 240
// how close to the bottom counts as "reading the newest message"
const MESSAGE_LIST_PIN_SLACK = 40

export default {
  name: 'MessageBoxV2026',
  props: {
    selectedConversation: Object
  },
  components: {
    Datepicker,
    OverlayCard,
    MessageTemplateMenuV2026,
    StickerPickerV2026,
    MonthCalendarV2026,
    DraftReviewOverlayV2026,
    BaseToast,
    FeedbackMenuV2026
  },
  data () {
    return {
      composerIcons: {
        camera: require('@/assets/icons2026/composer-camera.svg'),
        attach: require('@/assets/icons2026/composer-attach.svg'),
        video: require('@/assets/icons2026/composer-video.svg'),
        emoji: require('@/assets/icons2026/composer-emoji.svg'),
        mic: require('@/assets/icons2026/composer-mic.svg'),
        reminder: require('@/assets/icons2026/composer-reminder.svg'),
        send: require('@/assets/icons2026/composer-send.svg'),
        clock: require('@/assets/icons2026/clock.svg'),
        calendar: require('@/assets/icons2026/calendar.svg'),
        refresh: require('@/assets/icons2026/refresh.svg')
      },
      message: null,
      composerHint: null,
      composerHintTimer: null,
      fileUploads: null,
      isSending: false,
      queryParams: {
        page: 0,
        limit: 50,
        customer_type: 'customer',
        show_categories: 1,
        keyword: ''
      },
      searchKeywordTimeout: null,
      typingSignalTimeout: null,
      typingAdminTimeout: {},
      isTyping: false,
      stickersLine: null,
      stickerLine: null,
      selectedPackageId: '11537',
      groupPackageIds: null,
      activePackageId: null,
      typingAdmins: [],
      remindDate: '',
      remindTime: '',
      selectedRemind: -1,
      remindShortcuts: [
        { value: -1, label: 'เลือก' },
        { value: 15, label: 'อีก 15 นาที' },
        { value: 30, label: 'อีก 30 นาที' },
        { value: 45, label: 'อีก 45 นาที' },
        { value: 60, label: 'อีก 1 ชั่วโมง' },
        { value: 120, label: 'อีก 2 ชั่วโมง' },
        { value: 180, label: 'อีก 3 ชั่วโมง' }
      ],
      submitData: '',
      remindAt: '',
      showRemindCard: false,
      openPanel: null,
      dateFieldError: false,
      remindMonth: null,
      remindBtnEl: null,
      useMonthCalendarInReminder: true,
      // FALSE = the untouched b-dropdown below. Keep that branch.
      useV2026MessageTemplateMenu: true,
      // Separate from the flag above so either can roll back alone.
      useV2026StickerPicker: true,
      isRecording: false,
      speech: null,
      // version2026: composer geometry — set once the admin drags the grip, cleared with the height reset
      composerDragLatched: false,
      composerWrittenHeight: '',
      composerBaselineHeight: 0,
      composerResizeObserver: null,
      composerColumnObserver: null,
      // version2026: the top-edge drag snapshot, taken at pointerdown and never re-measured mid-drag
      composerResizing: null,
      // version2026: the full-screen draft review overlay. Click-only — a global shortcut is the one way
      // to get two BaseModals open at once, which would unlock document.body under the survivor.
      showDraftReview: false,
      warningLineId: `v2026-limit-warn-${++limitUid}`,
      limitAnnouncement: '',
      toast: { open: false, variant: 'danger', title: '', message: '', icon: 'alert' }
    }
  },
  computed: {
    ...mapState('admin', ['admin']),
    ...mapState('messageTemplate', ['messageTemplates', 'canLoadMore']),
    ...mapState('conversation', ['customerType']),
    ...mapState('aiAssistant', ['replyTranslateAi', 'composerInsert']),
    ...mapState('general', ['messageLimits', 'messageLimitLabels']),
    activeTextLimit () {
      const type = this.selectedConversation ? this.selectedConversation.type : null
      return getTextLimit(type, this.messageLimits)
    },
    platformLabel () {
      const type = this.selectedConversation ? this.selectedConversation.type : null
      return getPlatformLabel(type, this.messageLimitLabels)
    },
    textLength () {
      return countMessageLength(this.message)
    },
    limitState () {
      return getLimitState(this.textLength, this.activeTextLimit)
    },
    showLimitMeter () {
      return this.limitState !== 'none' && this.limitState !== 'hidden'
    },
    isOverTextLimit () {
      return this.limitState === 'over'
    },
    counterText () {
      return counterText(this.textLength, this.activeTextLimit)
    },
    warningText () {
      return warningText(this.platformLabel)
    },
    blockedSendDetail () {
      return blockedSendDetail(this.textLength, this.activeTextLimit, this.platformLabel)
    },
    // version2026: what the overlay's footer states is queued. Read-only there — removal stays in the
    // composer, where the badge and its × live. The badge itself only renders for exactly one file.
    draftAttachmentName () {
      if (!this.fileUploads || !this.fileUploads.length) return ''
      if (this.fileUploads.length === 1) return this.fileUploads[0].name
      return `${this.fileUploads.length} ไฟล์`
    },
    normalizedCustomerType () {
      if (this.customerType === 'professional_secondary' || this.customerType === 'professional') {
        return 'professional'
      }
      return this.customerType
    },
    shortcutLabel () {
      const hit = this.remindShortcuts.find(opt => opt.value === this.selectedRemind)
      return (hit || this.remindShortcuts[0]).label
    },
    timeGridOptions () {
      const options = []
      for (let minutes = 0; minutes < 24 * 60; minutes += 15) {
        const hh = String(Math.floor(minutes / 60)).padStart(2, '0')
        const mm = String(minutes % 60).padStart(2, '0')
        options.push(`${hh}:${mm}`)
      }
      return options
    },
    isTimeOffGrid () {
      return !!this.remindTime && !this.timeGridOptions.includes(this.remindTime)
    },
    remindCardRenderKey () {
      return [
        this.selectedRemind,
        this.remindDate,
        this.remindTime,
        this.remindAt,
        this.openPanel,
        this.remindMonth,
        this.dateFieldError
      ].join('|')
    },
    remindDateLabel () {
      return this.remindDate ? moment(this.remindDate).format('DD/MM/YYYY') : 'วว/ดด/ปปปป'
    },
    remindDateStr () {
      return this.remindDate ? moment(this.remindDate).format('YYYY-MM-DD') : null
    }
  },
  watch: {
    'selectedConversation.id': function (newVal, oldVal) {
      this.loadMessageTemplates()
      this.closeRemindCard()
      // the notice described the previous chat's draft
      this.setComposerHint(null)
      // version2026: the new chat always opens with a 2-row composer
      this.resetComposerHeight()
      // version2026: a silently re-pointed review surface is the dangerous state. The draft itself is
      // deliberately kept across chats — that is the composer's existing behaviour and it stays.
      this.showDraftReview = false
      this.$nextTick(() => {
        this.remindBtnEl = this.$refs.remindBtn || null
      })
    },
    message (newVal, oldVal) {
      if (newVal) {
        this.debounceSendTypingSignal()
      } else {
        // composer emptied — the notice no longer describes anything
        this.setComposerHint(null)
      }
      // version2026: re-fit after the textarea has the new value
      this.$nextTick(this.fitComposerHeight)
    },
    // version2026: the hint takes height the cap was not counting
    composerHint () {
      this.$nextTick(this.fitComposerHeight)
    },
    // version2026: so does the limit row, twice — once when the row appears, and again when the
    // warning line joins it
    showLimitMeter () {
      this.$nextTick(this.fitComposerHeight)
    },
    isOverTextLimit () {
      this.$nextTick(this.fitComposerHeight)
    },
    // version2026: announce ONCE on crossing 80%. 'idle' must stay in the clear list — otherwise
    // re-crossing 80% assigns the identical string, re-renders nothing, and never announces again.
    limitState (newVal, oldVal) {
      if (newVal === 'neutral' && oldVal !== 'neutral' && oldVal !== 'warning') {
        this.$nextTick(() => {
          this.limitAnnouncement = nearLimitAnnouncement(this.activeTextLimit, this.platformLabel)
        })
      } else if (newVal === 'idle' || newVal === 'hidden' || newVal === 'none' || newVal === 'over') {
        this.limitAnnouncement = ''
      }
    },
    // version2026: so does each "กำลังพิมพ์" row
    'typingAdmins.length': function () {
      this.$nextTick(this.fitComposerHeight)
    },
    'speech.result': function (newVal, oldVal) {
      this.message = newVal
    },
    'speech.isListening': function (newVal, oldVal) {
      if (newVal === false) {
        this.stopSpeechRecognition()
        this.isRecording = false
      }
    },
    replyTranslateAi () {
      if (this.replyTranslateAi) {
        this.message = this.replyTranslateAi
      }
    },
    composerInsert (payload) {
      this.applyComposerInsert(payload)
    }
  },
  filters: {
    onlyTime: function (date) {
      if (!date) {
        return
      }
      return moment(date).format('HH:mm')
    },
    displayRemindDate: function (date) {
      let today = moment().format('YYYY-MM-DD')
      let onlyDate = moment(date).format('YYYY-MM-DD')
      if (!date) {
        return
      }
      if (today === onlyDate) {
        return moment(date).format('HH:mm')
      } else {
        return moment(date).format('YYYY-MM-DD HH:mm')
      }
    }
  },
  methods: {
    ...mapActions('messageTemplate', ['fetchMessageTemplates']),
    getFileExtension (url) {
      return url.split(/[#?]/)[0].split('.').pop().trim()
    },
    checkMic () {
      this.isRecording = !this.isRecording
      if (this.isRecording) {
        this.$refs.message.focus()
        this.startSpeechRecognition()
      } else {
        this.stopSpeechRecognition()
      }
    },
    startSpeechRecognition () {
      if (this.speech && this.speech.isSupported) {
        this.speech.start()
      }
    },
    stopSpeechRecognition () {
      if (this.speech) {
        this.speech.stop()
      }
    },
    maskStyle (url) {
      return { '-webkit-mask-image': 'url(' + url + ')', 'mask-image': 'url(' + url + ')' }
    },
    // Never overwrites what the admin typed; the sender reads back `appended` for its toast.
    applyComposerInsert (payload) {
      if (!payload || !payload.text) return
      const current = this.message || ''
      payload.appended = current.trim().length > 0
      this.message = payload.appended ? current + '\n\n' + payload.text : payload.text
      this.$nextTick(() => {
        const el = this.$refs.message
        if (!el) return
        el.focus()
        el.setSelectionRange(el.value.length, el.value.length)
      })
      // version2026: opt-in — a payload without `hint` clears to null, so other writers are unaffected
      this.setComposerHint(payload.appended && payload.hint ? payload.hint : null)
    },
    // version2026: single owner of the append notice's lifetime
    setComposerHint (hint) {
      if (this.composerHintTimer) {
        clearTimeout(this.composerHintTimer)
        this.composerHintTimer = null
      }
      this.composerHint = hint || null
      if (!this.composerHint) return
      this.composerHintTimer = setTimeout(() => {
        this.composerHint = null
        this.composerHintTimer = null
      }, COMPOSER_HINT_TTL_MS)
    },
    // version2026: the .message-display element, via the panel's public accessor when it is there
    getMessageListEl () {
      if (this.$parent && typeof this.$parent.getMessageScrollEl === 'function') {
        return this.$parent.getMessageScrollEl()
      }
      if (!this.$el || !this.$el.parentElement) return null
      return this.$el.parentElement.querySelector('.message-display')
    },
    // version2026: the rows="2" height, measured once and used as the floor of both stops
    getComposerBaselineHeight (el) {
      if (this.composerBaselineHeight) return this.composerBaselineHeight
      const inlineHeight = el.style.height
      const inlineMaxHeight = el.style.maxHeight
      el.style.height = ''
      el.style.maxHeight = ''
      this.composerBaselineHeight = el.offsetHeight
      el.style.height = inlineHeight
      el.style.maxHeight = inlineMaxHeight
      return this.composerBaselineHeight
    },
    // version2026: how much height the list and the textarea share — measured off the column, because a
    // clipped list under-reports its own size and would leave the bar hanging below the viewport
    getComposerBudget (el) {
      const listEl = this.getMessageListEl()
      const columnEl = this.$el ? this.$el.parentElement : null
      if (!listEl || !columnEl) return 0
      let siblings = 0
      Array.prototype.forEach.call(columnEl.children, child => {
        if (child === listEl || child === this.$el) return
        siblings += child.offsetHeight
      })
      // everything the composer holds besides the textarea: hint, typing rows, divider, bar, padding
      const composerChrome = this.$el.offsetHeight - el.offsetHeight
      return columnEl.clientHeight - siblings - composerChrome
    },
    // version2026: the least the list can render in — its own padding and border. The list may give up
    // every pixel of content, so this is measured, not chosen; the constant is only the fallback
    getMessageListMinHeight () {
      const listEl = this.getMessageListEl()
      const cs = listEl && typeof window.getComputedStyle === 'function'
        ? window.getComputedStyle(listEl)
        : null
      const box = cs
        ? parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom) +
          parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth)
        : NaN
      // the constant is consumed here and nowhere else — one derivation, two affordances, one literal
      return isFinite(box) ? box : MESSAGE_LIST_FLOOR
    },
    // version2026: the one place the composer's hard ceiling is derived — every sizing path calls this
    getComposerHardMax (el) {
      const baseline = this.getComposerBaselineHeight(el)
      const budget = this.getComposerBudget(el)
      // 0 = the column cannot be measured yet, so nothing is clamped
      if (budget <= 0) return 0
      return Math.max(budget - this.getMessageListMinHeight(), baseline)
    },
    // version2026: true when the admin is looking at the newest message
    isMessageListPinned () {
      const listEl = this.getMessageListEl()
      if (!listEl) return false
      return listEl.scrollHeight - listEl.scrollTop - listEl.clientHeight <= MESSAGE_LIST_PIN_SLACK
    },
    // version2026: keep the newest message glued to the bottom after the list changes height
    repinMessageList () {
      if (this.$parent && typeof this.$parent.scrollMessageToBottom === 'function') {
        this.$parent.scrollMessageToBottom()
        return
      }
      const listEl = this.getMessageListEl()
      if (listEl) listEl.scrollTop = listEl.scrollHeight
    },
    // version2026: fit the box to its content, and cap it so the conversation keeps its floor
    fitComposerHeight () {
      const el = this.$refs.message
      if (!el) return
      if (!this.message && !this.composerDragLatched) {
        this.resetComposerHeight()
        return
      }
      const baseline = this.getComposerBaselineHeight(el)
      const budget = this.getComposerBudget(el)
      let autoStop = 0
      if (budget > 0) {
        // the box may take exactly what the list can afford to give up
        el.style.maxHeight = this.getComposerHardMax(el) + 'px'
        autoStop = Math.max(budget - MESSAGE_LIST_AUTO_RESERVE, baseline)
      }
      // a dragged height is the admin's decision — auto-grow stands down until the reset
      if (this.composerDragLatched) return
      const wasPinned = this.isMessageListPinned()
      el.style.height = 'auto'
      const next = autoStop ? Math.min(el.scrollHeight, autoStop) : el.scrollHeight
      el.style.height = next + 'px'
      this.composerWrittenHeight = el.style.height
      if (wasPinned) this.repinMessageList()
    },
    // version2026: back to the rows="2" baseline — instant, no transition
    resetComposerHeight ({ repin = false } = {}) {
      this.composerDragLatched = false
      this.composerWrittenHeight = ''
      const el = this.$refs.message
      if (!el) return
      el.style.height = ''
      el.style.maxHeight = ''
      if (repin) this.repinMessageList()
    },
    // version2026: the browser writes its own inline height on drag — a mismatch means the admin did it
    detectComposerDrag () {
      const el = this.$refs.message
      if (!el || this.composerDragLatched) return
      if (el.style.height !== this.composerWrittenHeight) {
        this.composerDragLatched = true
      }
    },
    // version2026: the cap is derived from live sibling sizes, so it has to follow the window
    onWindowResizeComposer () {
      this.fitComposerHeight()
    },
    // version2026: one observer, two jobs, told apart by target. The textarea entry is the latch
    // detector; every other entry is a sibling of the composer inside .v2026-col-main, and a sibling
    // that grew has just eaten the cushion the ceiling used to leave. While latched fitComposerHeight
    // only rewrites max-height, so an over-tall box is pulled in without overriding the dragged height.
    onComposerObserved (entries) {
      let refit = false
      for (const entry of entries) {
        if (entry.target === this.$refs.message) {
          this.detectComposerDrag()
        } else {
          refit = true
        }
      }
      if (refit) this.fitComposerHeight()
    },
    // version2026: point the observer at whatever the column is holding right now. Measured: watching
    // the message list alone is not enough — at the ceiling the list is already at its irreducible 48px
    // and cannot shrink, so it never reports. The ดาวน์โหลดไฟล์ strip is a v-if and does not exist to be
    // observed until it appears, hence the childList watch in mounted() that calls this again.
    syncComposerSiblingObserver () {
      const observer = this.composerResizeObserver
      const columnEl = this.$el ? this.$el.parentElement : null
      if (!observer || !columnEl) return
      Array.prototype.forEach.call(columnEl.children, child => {
        if (child === this.$el) return
        observer.observe(child)
      })
    },
    // version2026: drag the top edge — the boundary the admin is actually looking at. The box is
    // bottom-anchored, so pointer up = taller. The height is draft-scoped and is never persisted.
    onComposerResizeStart (ev) {
      // the handle is hidden below this width; guard the event too, same as the column dividers
      if (window.innerWidth <= 820) return
      const el = this.$refs.message
      if (!el) return
      ev.preventDefault()
      const max = this.getComposerHardMax(el)
      this.composerResizing = {
        startY: ev.clientY,
        startHeight: el.offsetHeight,
        min: this.getComposerBaselineHeight(el),
        max,
        pinned: this.isMessageListPinned()
      }
      // the same ceiling auto-grow writes — one derivation, two affordances
      if (max > 0) el.style.maxHeight = max + 'px'
      // a deliberate resize — auto-grow stands down until the reset
      this.composerDragLatched = true
      this.setComposerDragCursor(true)
      if (ev.target.setPointerCapture) ev.target.setPointerCapture(ev.pointerId)
    },
    onComposerResizeMove (ev) {
      const r = this.composerResizing
      if (!r) return
      const el = this.$refs.message
      if (!el) return
      const ceiling = r.max > 0 ? r.max : Number.MAX_SAFE_INTEGER
      const next = Math.round(Math.min(Math.max(r.startHeight + (r.startY - ev.clientY), r.min), ceiling))
      el.style.height = next + 'px'
      // keep the latch detector in step with our own write
      this.composerWrittenHeight = el.style.height
      if (r.pinned) this.repinMessageList()
    },
    onComposerResizeEnd (ev) {
      const r = this.composerResizing
      if (!r) return
      this.composerResizing = null
      this.setComposerDragCursor(false)
      if (ev.target.releasePointerCapture && ev.target.hasPointerCapture && ev.target.hasPointerCapture(ev.pointerId)) {
        ev.target.releasePointerCapture(ev.pointerId)
      }
      if (r.pinned) this.repinMessageList()
    },
    // version2026: the overlay is opened by click only — see the showDraftReview comment in data()
    openDraftReview () {
      this.showDraftReview = true
    },
    closeDraftReview () {
      this.showDraftReview = false
      this.$nextTick(() => {
        if (this.$refs.draftReviewBtn) this.$refs.draftReviewBtn.focus()
      })
    },
    // version2026: the overlay holds no copy — this is the whole write path back into the draft
    onDraftReviewInput (value) {
      this.message = value
    },
    // version2026: the SAME method .v2026-send calls. No second send implementation exists.
    async onDraftReviewSend () {
      await this.sendMessage()
      // success empties the draft and closes; a failure keeps both the text and the overlay
      if (!this.message) this.closeDraftReview()
    },
    // version2026: double-click gives the automatic behaviour back, like the column dividers' reset
    resetComposerDragHeight () {
      this.resetComposerHeight()
      this.fitComposerHeight()
    },
    // version2026: during a drag nothing else on the page may be selected and the cursor must not flicker
    setComposerDragCursor (on) {
      const b = document.body
      if (!b) return
      b.style.userSelect = on ? 'none' : ''
      b.style.cursor = on ? 'row-resize' : ''
    },
    toggleRemindCard () {
      if (this.showRemindCard) {
        this.closeRemindCard({ returnFocus: true })
        return
      }
      this.showRemindCard = true
      this.openPanel = null
      this.dateFieldError = false
      this.remindMonth = moment(this.remindDate || undefined).format('YYYY-MM')
      this.$nextTick(() => {
        if (this.$refs.shortcutSelect) this.$refs.shortcutSelect.focus()
      })
    },
    closeRemindCard ({ returnFocus = false } = {}) {
      this.showRemindCard = false
      this.openPanel = null
      this.dateFieldError = false
      if (returnFocus) {
        this.$nextTick(() => {
          if (this.$refs.remindBtn) this.$refs.remindBtn.focus()
        })
      }
    },
    onEsc (e) {
      if (!this.showRemindCard) return
      e.stopPropagation()
      if (this.openPanel) {
        const back = this.openPanel === 'time' ? 'remindTimeInput' : 'remindDateBtn'
        this.openPanel = null
        this.$nextTick(() => {
          if (this.$refs[back]) this.$refs[back].focus()
        })
        return
      }
      this.closeRemindCard({ returnFocus: true })
    },
    onFocusOut (e) {
      if (!this.showRemindCard) return
      const next = e.relatedTarget
      if (!next) return
      if (this.$el.contains(next)) return
      if (next.closest && next.closest('.swal2-container')) return
      this.closeRemindCard({ returnFocus: false })
    },
    async removeReminder () {
      this.$swal({
        text: 'คุณต้องการลบเตือนนัดหมายของบทสนทนานี้ ใช่หรือไม่ ?',
        showCancelButton: true,
        confirmButtonText: 'ใช่ ',
        cancelButtonText: 'ไม่'
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            await axios.delete('/conversations/' + this.selectedConversation.id + '/reminder?admin_id=' + this.admin.id)
            this.remindAt = ''
            this.$swal({
              text: 'ลบการแจ้งเตือนสำเร็จ',
              type: 'success'
            })
          } catch (err) {
            this.$swal({
              text: 'ผิดพลาด ' + err.message,
              type: 'error'
            })
          }
        }
      })
    },
    togglePanel (panel) {
      const next = this.openPanel === panel ? null : panel
      this.openPanel = next
      if (next === 'date') {
        this.remindMonth = moment(this.remindDate || undefined).format('YYYY-MM')
      }
      if (!next) return
      this.$nextTick(() => {
        if (next === 'time') this.scrollTimeListToValue()
        const panelEl = next === 'time' ? this.$refs.remindTimePanel : this.$refs.remindDatePanel
        if (panelEl) panelEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
      })
    },
    openTimeList () {
      if (this.openPanel === 'time') return
      this.togglePanel('time')
    },
    scrollTimeListToValue () {
      const panelEl = this.$refs.remindTimePanel
      if (!panelEl) return
      const rowHeight = 32
      const current = this.remindTime || moment().format('HH:mm')
      let index = this.timeGridOptions.indexOf(current)
      if (index < 0) {
        index = 0
        for (let i = 0; i < this.timeGridOptions.length; i++) {
          if (this.timeGridOptions[i] <= current) index = i
        }
      }
      const offset = this.isTimeOffGrid ? rowHeight + 9 : 0
      panelEl.scrollTop = Math.max(0, offset + index * rowHeight)
    },
    pickTime (t) {
      this.remindTime = t
      this.openPanel = null
      this.$nextTick(() => {
        if (this.$refs.remindTimeInput) this.$refs.remindTimeInput.focus()
      })
    },
    async submitConfirm () {
      let submitData = {}
      submitData.remind_date = moment(this.remindDate).format('YYYY-MM-DD')
      if (this.remindTime) {
        submitData.remind_date += ` ${this.remindTime}:00`
      }

      let checkIsValidDate = moment(submitData.remind_date, 'YYYY-MM-DD HH:mm:ss', true).isValid()
      if (!checkIsValidDate) {
        this.$swal({
          text: 'รูปแบบการตั้งเวลาไม่ถูกต้อง กรุณาตรวจสอบอีกครั้ง',
          type: 'error'
        })
        if (!this.remindDate) this.flagMissingDate()
        return false
      }

      let checkIsAfterToday = moment().isSameOrAfter(submitData.remind_date)
      if (checkIsAfterToday) {
        this.$swal({
          text: 'ไม่สามารถตั้งเวลาเตือนย้อนหลังได้',
          type: 'error'
        })
        return false
      }

      if (!this.remindDate) {
        this.flagMissingDate()
        return false
      }

      if (!this.remindDate && !this.remindTime) {
        submitData.remind_date = ''
      }
      const { data } = await axios.post(`/conversations/${this.selectedConversation.id}/reminder`, {
        remind_at: submitData.remind_date,
        admin_id: this.admin.id
      })
      if (!data.error) {
        this.remindAt = data.conversation.remind_at
        if (data.conversation.remind_at == null) {
          this.$swal({
            text: 'ยกเลิกการแจ้งเตือน',
            type: 'success'
          })
        } else {
          this.$swal({
            text: 'ตั้งเวลาแจ้งเตือนสำเร็จ',
            type: 'success'
          })
        }
        this.closeRemindCard({ returnFocus: false })
      } else {
        this.$swal({
          text: 'ผิดพลาด',
          type: 'error'
        })
      }
    },
    clearReminder () {
      this.selectedRemind = -1
      this.remindDate = ''
      this.remindTime = ''
    },
    clearRemindDate () {
      this.remindDate = ''
      this.selectedRemind = -1
      this.$nextTick(() => {
        if (this.$refs.remindDateBtn) this.$refs.remindDateBtn.focus()
      })
    },
    onShortcutChange (event) {
      const minutes = event.target.value
      if (minutes < 0) {
        this.remindDate = ''
        this.remindTime = ''
        return
      }
      let datetime = moment().add(minutes, 'minutes').format('YYYY-MM-DD HH:mm')
      const comp = datetime.split(' ')
      this.remindDate = comp[0]
      this.remindTime = comp[1]
    },
    changeInput () {
      this.selectedRemind = -1
      this.remindTime = ''
    },
    onRemindDateSelect (dateStr) {
      this.remindDate = dateStr
      this.dateFieldError = false
      this.changeInput()
      this.openPanel = null
      this.$nextTick(() => {
        if (this.$refs.remindDateBtn) this.$refs.remindDateBtn.focus()
      })
    },
    flagMissingDate () {
      if (this.useMonthCalendarInReminder) {
        this.dateFieldError = true
        this.openPanel = 'date'
        this.remindMonth = moment().format('YYYY-MM')
        this.$nextTick(() => {
          if (this.$refs.remindDateBtn) this.$refs.remindDateBtn.focus()
          if (this.$refs.remindDatePanel) this.$refs.remindDatePanel.scrollIntoView({ block: 'nearest' })
        })
      } else {
        this.$refs.remind_date_picker.$el.querySelector('input').focus()
        this.$refs.remind_date_picker.showCalendar()
      }
    },
    async searchKeyword (keyword) {
      const queryParams = { ...this.queryParams }
      queryParams.force_fetch_data = true
      queryParams.keyword = keyword
      queryParams.page = 0
      await this.fetchMessageTemplates(queryParams)
      this.$forceUpdate()
    },
    selectPackageId (packageId) {
      this.selectedPackageId = packageId
    },
    selectSticker (sticker) {
      this.stickerLine = sticker
      this.sendMessage()
      this.$refs.dropdownStickeLine.hide(true)
    },
    async fetchStickers () {
      const { data } = await axios.get('/sticker-line')
      this.stickersLine = data.stickers
      this.groupPackageIds = data.groupPackageIds
    },
    clearMessage () {
      this.message = null
      this.fileUploads = null
      this.stickerLine = null
      this.isSending = false
      this.resetComposerHeight()
      this.queryParams = {
        page: 0,
        limit: 50,
        // keep the conversation's type: this can run after loadMessageTemplates has set it, and a
        // hardcoded 'customer' would send every later search and load-more to the customer list
        customer_type: this.selectedConversation ? this.selectedConversation.customer_type : 'customer',
        show_categories: 1,
        keyword: ''
      }
    },
    debounceSearchKeyword (keyword) {
      if (this.searchKeywordTimeout) clearTimeout(this.searchKeywordTimeout)
      this.searchKeywordTimeout = setTimeout(() => {
        this.searchKeyword(keyword)
      }, 400)
    },
    debounceSendTypingSignal () {
      if (this.isTyping) {
        return
      }
      this.isTyping = true
      this.sendTypingSignal()
      if (this.typingSignalTimeout) clearTimeout(this.typingSignalTimeout)
      this.typingSignalTimeout = setTimeout(() => {
        this.isTyping = false
      }, 5000)
    },
    async sendTypingSignal () {
      await axios.post(`/conversations/${this.selectedConversation.id}/typing`, { admin_id: this.admin.id })
      return Promise.resolve(1)
    },
    getExtension (filename) {
      const parts = filename.split('.')
      return parts[parts.length - 1]
    },
    isImage (ext) {
      switch (ext.toLowerCase()) {
        case 'jpg':
        case 'gif':
        case 'jpeg':
        case 'png':
          return true
      }
      return false
    },
    removeFileUpload (index) {
      this.fileUploads = null
    },
    handleFileUpload (fileType) {
      let valid = true
      this.fileUploads = this.$refs.imageFiles.files
      if (fileType === 'pdf') {
        this.fileUploads = this.$refs.pdfFiles.files
      } else if (fileType === 'mp4') {
        this.fileUploads = this.$refs.mp4Files.files
      }
      const fileCount = this.fileUploads.length
      for (const file of this.fileUploads) {
        const ext = this.getExtension(file.name)
        switch (fileType) {
          case 'image':
            if (!this.isImage(ext)) {
              valid = false
            }
            break
          case 'pdf':
            if (ext !== 'pdf') {
              valid = false
            }
            break
          case 'mp4':
            if (ext !== 'mp4') {
              valid = false
            }
            break
        }
      }

      if (valid) {
        if (fileCount > 1) {
          this.sendFileUploads()
        }
      } else {
        this.fileUploads = null
        this.$swal({ text: 'ประเภทของไฟล์ไม่ถูกต้อง', icon: 'error' })
      }
    },
    async sendFileUploads () {
      try {
        for (const file of this.fileUploads) {
          let formData = new FormData()
          formData.append('fileUpload', file)
          formData.append(
            'conversation',
            JSON.stringify(this.selectedConversation)
          )
          formData.append('adminId', this.admin.id)
          const ext = this.getExtension(file.name)
          let url = '/messages/upload'
          if (ext === 'pdf') {
            url = '/messages/upload-pdf'
          } else if (ext === 'mp4') {
            url = '/messages/upload-mp4'
          }
          await axios.post(url, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
        }
      } catch (e) {
        this.$swal({ text: 'ไม่สามารถส่งข้อความได้', icon: 'error' })
        console.log('check error', e)
      }
      this.fileUploads = null
    },
    async sendMessage () {
      // Covers the send button, Shift+Enter and the draft-review overlay (which delegates here),
      // before isSending and before any axios call. It never touches this.message /
      // this.stickerLine / this.fileUploads, so the draft stays put.
      if (this.isOverTextLimit) {
        this.openToast('ส่งไม่ได้', this.blockedSendDetail)
        return
      }
      this.isSending = true
      try {
        if (this.fileUploads) {
          await this.sendFileUploads()
        }
        if (this.message || this.stickerLine) {
          let url = '/messages'
          const submitData = {
            conversation: this.selectedConversation,
            adminId: this.admin.id
          }
          if (this.stickerLine) {
            url = '/messages/sticker-line'
            submitData.stickerLine = this.stickerLine
          } else {
            if (this.isMessageTemplate()) {
              url = '/messages/template'
            }
            submitData.message = this.message
          }
          const res = await axios.post(url, submitData)
          if (res.status === 200) {
            this.message = null
            this.stickerLine = null
          }
        }
      } catch (e) {
        console.log('check error', e.message)
        // the server's own error string already names the real platform, limit and length
        const res = e.response
        if (res && res.status === 400 && res.data && res.data.reason === 'MESSAGE_TOO_LONG') {
          this.openToast('ส่งไม่ได้', res.data.error)
        } else {
          this.$swal({ text: 'ไม่สามารถส่งข้อความได้', icon: 'error' })
        }
      }
      // version2026: collapse only once the draft is really gone — a failed send keeps text and height
      if (!this.message) this.resetComposerHeight({ repin: true })
      this.isSending = false
    },
    // version2026: one toast at a time — re-opening on the next tick re-arms BaseToast's timer
    // without ever stacking two instances.
    openToast (title, message) {
      this.toast.open = false
      this.$nextTick(() => {
        this.toast.title = title
        this.toast.message = message
        this.toast.variant = 'danger'
        this.toast.icon = 'alert'
        this.toast.open = true
      })
    },
    isMessageTemplate () {
      const reg = /^\/\d+$/
      const result = this.message.match(reg)
      return !!result
    },
    selectMessageTemplate (messageTemplate) {
      if (messageTemplate.type === 'text') {
        this.message = `${messageTemplate.message}`
        if (messageTemplate.attachment) {
          this.message = `/${messageTemplate.key}`
        }
      } else {
        this.message = `/${messageTemplate.key}`
      }

      this.$refs.dropdown.hide(true)
    },
    handleScroll ({ target: { scrollTop, clientHeight, scrollHeight } }) {
      if (scrollTop + clientHeight >= scrollHeight) {
        this.queryParams.page += 1
        this.fetchMessageTemplates(this.queryParams)
      }
    },
    loadMessageTemplates () {
      this.queryParams.customer_type = this.selectedConversation
        ? this.selectedConversation.customer_type
        : 'customer'
      this.fetchMessageTemplates(this.queryParams)
      if (this.selectedConversation) {
        this.remindAt = this.selectedConversation.remind_at
        this.remindDate = ''
        this.remindTime = ''
        if (this.selectedConversation.remind_at) {
          this.remindDate = moment(this.selectedConversation.remind_at).format('YYYY-MM-DD')
          this.remindTime = moment(this.selectedConversation.remind_at).format('HH:mm')
        }
      }
    },
    async sendCustomerChatTopicTags () {
      const submitData = {
        conversation: this.selectedConversation,
        adminId: this.admin.id
      }
      await axios.post('/messages/buttons', submitData)
    }
  },
  mounted () {
    this.loadMessageTemplates()
    this.remindBtnEl = this.$refs.remindBtn || null
    window.addEventListener('resize', this.onWindowResizeComposer)
    // version2026: watches the textarea so a manual drag can be told apart from our own writes, and the
    // composer's siblings so their growth re-clamps the box in the same frame
    if (typeof ResizeObserver !== 'undefined' && this.$refs.message) {
      this.composerResizeObserver = new ResizeObserver(this.onComposerObserved)
      this.composerResizeObserver.observe(this.$refs.message)
      // the siblings live in the parent, so they are only guaranteed on the next tick
      this.$nextTick(this.syncComposerSiblingObserver)
    }
    // version2026: the ดาวน์โหลดไฟล์ strip is added and removed by a v-if, so its arrival is a childList
    // change, not a resize — the size guard above cannot see it without this
    if (typeof MutationObserver !== 'undefined' && this.$el && this.$el.parentElement) {
      this.composerColumnObserver = new MutationObserver(() => {
        this.syncComposerSiblingObserver()
        this.fitComposerHeight()
      })
      this.composerColumnObserver.observe(this.$el.parentElement, { childList: true })
    }
    this.speech = useSpeechRecognition({
      lang: 'th-TH',
      interimResults: true,
      continuous: true
    })
  },
  beforeDestroy () {
    // version2026: never leave the append-notice timer pending
    if (this.composerHintTimer) {
      clearTimeout(this.composerHintTimer)
      this.composerHintTimer = null
    }
    window.removeEventListener('resize', this.onWindowResizeComposer)
    // version2026: unmounting mid-drag must never leave the whole app unselectable
    this.composerResizing = null
    this.setComposerDragCursor(false)
    if (this.composerResizeObserver) {
      this.composerResizeObserver.disconnect()
      this.composerResizeObserver = null
    }
    if (this.composerColumnObserver) {
      this.composerColumnObserver.disconnect()
      this.composerColumnObserver = null
    }
  },
  created () {
    this.fetchStickers()
  },
  sockets: {
    connect () {
    },
    disconnect () {
      console.log('socket disconnected')
    },
    onTyping (data) {
      if (!this.selectedConversation || Number(this.selectedConversation.id) !== Number(data.conversationId)) {
        return
      }
      const admin = data.admin
      if (this.admin.id === admin.id) {
        return
      }
      let typingAdmin = this.typingAdmins.find(item => item.id === admin.id)
      if (!typingAdmin) {
        this.typingAdmins.push(admin)
      }
      if (this.typingAdminTimeout[admin.id]) clearTimeout(this.typingAdminTimeout[admin.id])
      this.typingAdminTimeout[admin.id] = setTimeout(() => {
        this.typingAdmins = this.typingAdmins.filter(item => item.id !== admin.id)
      }, 3000)
    }
  }
}
</script>

<style lang="scss" scoped>
.v2026-composer {
  --v2026-red:            #F04438;
  --v2026-teal:           #12A89F;
  --v2026-teal-hover:     #159C96;
  --v2026-surface:        #FFFFFF;
  --v2026-surface-sunken: #F2F4F7;
  --v2026-surface-hover:  #E9ECF1;
  --v2026-border:         #E4E7EC;
  --v2026-text:           #101828;
  --v2026-text-secondary: #667085;
  --v2026-text-muted:     #98A2B3;
  --v2026-icon:           #475467;
  --v2026-r-pill:   999px;
  --v2026-r-btn:    10px;
  --v2026-r-box:    14px;
  --v2026-sp-2: 8px;
  --v2026-sp-3: 12px;
  --v2026-sp-4: 16px;
  --v2026-sp-5: 20px;
  --v2026-fs-ctrl: 13px;
  --v2026-fs-body: 14px;

  padding: var(--v2026-sp-3);
  border: 1px solid var(--v2026-border);
  // containing block for the top-edge handle. No z-index — it would trap the reminder card underneath.
  position: relative;
}

// version2026: เส้นลากปรับความสูง — เส้นบาง แต่ hit area กว้างกว่าด้วย ::before
.v2026-composer__resizer {
  position: absolute;
  left: 0;
  right: 0;
  top: -3px;
  height: 5px;
  cursor: row-resize;
  background: transparent;
  touch-action: none;
  z-index: 5;
}

.v2026-composer__resizer::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: -3px;
  bottom: -3px;
}

.v2026-composer__resizer::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 2px;
  height: 1px;
  background: transparent;
  transition: background-color .15s ease;
}

.v2026-composer__resizer:hover::after,
.v2026-composer__resizer.is-resizing::after {
  background: var(--v2026-color-main, #31C1D7);
}

@media (max-width: 820px) {
  .v2026-composer__resizer { display: none; }
}

.v2026-composer__input {
  width: 100%;
  border: 0;
  background: transparent;
  resize: vertical;
  padding: var(--v2026-sp-2);
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text);

  &::placeholder {
    color: var(--v2026-text-muted);
  }
  &:focus {
    outline: none;
    box-shadow: none;
  }
  // version2026: paint the native grip. Strokes only — no fill/border/padding, so the resting height cannot move.
  &::-webkit-resizer {
    background-image: repeating-linear-gradient(
      135deg,
      transparent 0 4px,
      var(--v2026-text-muted) 4px 5px
    );
  }
  &:hover::-webkit-resizer,
  &:focus::-webkit-resizer {
    background-image: repeating-linear-gradient(
      135deg,
      transparent 0 4px,
      var(--v2026-text-secondary) 4px 5px
    );
  }
}

// Teal, not red: a heads-up, not an error. Must stay wrappable — it grows the composer at narrow widths.
.v2026-composer__hint {
  margin-top: 4px;
  padding: 6px var(--v2026-sp-2);
  border-left: 3px solid var(--v2026-teal);
  border-radius: 6px;
  background: var(--v2026-surface-sunken);
  color: var(--v2026-text-secondary);
  font-size: var(--v2026-fs-ctrl);
  line-height: 1.3;
}

// Own hex values, not --v2026-red (#F04438): 3.4:1 on white, so it fails AA at 12px.
.v2026-limit {
  --v2026-amber-text:  #B54708;
  --v2026-danger-text: #B42318;

  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 4px var(--v2026-sp-3);
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.3;
  color: var(--v2026-text-secondary);
}
// Empty on purpose: 'idle' is styled like 'neutral', already the quietest legible treatment — the one
// axis left, a lighter grey, fails AA (--v2026-text-muted #98A2B3 is 2.5:1). The hook keeps the band.
.v2026-limit--idle    { }
.v2026-limit--warning { color: var(--v2026-amber-text); font-weight: 600; }
.v2026-limit--over    { color: var(--v2026-danger-text); font-weight: 600; }

.v2026-limit__warn {
  flex: 1 1 auto;
  text-align: left;
}
.v2026-limit__count {
  margin-left: auto;
  text-align: right;
}
.v2026-limit__glyph {
  vertical-align: -2px;
  margin-right: 4px;
}
.v2026-limit__nums {
  margin-left: 4px;
  white-space: nowrap;
}

@media (max-width: 575.98px) {
  .v2026-limit { font-size: 11px; }
}

.v2026-composer__divider {
  border: 0;
  border-top: 1px solid var(--v2026-border);
  margin: var(--v2026-sp-2) 0;
}

.v2026-composer__bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--v2026-sp-2);
}

.v2026-composer__left,
.v2026-composer__right {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  flex-wrap: wrap;
}

.v2026-file-badge {
  display: flex;
  align-items: center;
}

.v2026-icon-btn,
::v-deep .v2026-icon-btn {
  width: 36px;
  height: 36px;
  min-width: 36px;
  padding: 0;
  border: 0;
  border-radius: var(--v2026-r-pill);
  background: transparent;
  color: var(--v2026-icon);
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: none;
  text-decoration: none;

  font-family: inherit;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover, &:focus, &:active {
    background: var(--v2026-surface-hover);
    color: var(--v2026-icon);
    box-shadow: none;
  }
}

::v-deep .v2026-icon-btn--chev {
  width: auto;
  min-width: 36px;
  padding: 0 var(--v2026-sp-2);
  gap: 4px;
}

.v2026-icon-btn--recording {
  width: auto;
  padding: 0 var(--v2026-sp-3);
  background: var(--v2026-red);
  color: #fff;

  &:hover {
    background: var(--v2026-red);
    color: #fff;
  }
}

.v2026-icon-btn__svg {
  width: 18px;
  height: 18px;
  display: inline-block;
  flex-shrink: 0;
  background-color: currentColor;
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
}

.v2026-mic-hint {
  font-size: var(--v2026-fs-ctrl);
}

.v2026-remind-label {
  color: var(--v2026-text-secondary);
}

.v2026-chev {
  font-size: 10px;
  color: var(--v2026-text-muted);
}

::v-deep .v2026-keylad,
::v-deep .v2026-emoji {
  height: 32px;
  padding: 0 var(--v2026-sp-3);
  border: 1px solid var(--v2026-border);
  background: var(--v2026-surface);
  color: var(--v2026-text-secondary);
  font-size: var(--v2026-fs-ctrl);
  text-decoration: none;
  box-shadow: none;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  &:hover, &:focus, &:active {
    background: var(--v2026-surface-hover);
    color: var(--v2026-text);
    box-shadow: none;
  }
}

.v2026-send {
  height: 40px;
  padding: 6px 14px;
  border: 0;
  border-radius: 8px;
  background: #31C1D7;
  color: #fff;
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  opacity: 1;
  display: inline-flex;
  align-items: center;
  gap: var(--v2026-sp-2);
  transition: background-color 150ms ease, opacity 150ms ease;

  &:hover:not(:disabled) {
    background: #2BAFC4;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.v2026-send__svg {
  width: 18px;
  height: 18px;
}

.v2026-remind-anchor {
  position: relative;
  display: inline-flex;
}

.v2026-remind-anchor .v2026-remind-pos {
  --v2026-remind-card-w:     352px;
  --v2026-remind-field-h:    40px;
  --v2026-remind-confirm-bg: var(--v2026-teal, #12A89F);

  bottom: calc(100% + var(--v2026-sp-2));
  right: 0;
  width: var(--v2026-remind-card-w);
  max-width: calc(100vw - 24px);
  max-height: min(60vh, 520px);
  overflow-y: auto;
}

.v2026-remind__title {
  display: flex;
  align-items: center;
  gap: var(--v2026-sp-2);
}

.v2026-remind__title-ico {
  width: 18px;
  height: 18px;
}

.v2026-remind__title-text {
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  color: var(--v2026-text);
}

.v2026-remind__delete {
  margin-left: auto;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--v2026-red);
  font-family: inherit;
  font-size: 12px;
  line-height: 1;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.v2026-remind__form {
  margin-top: var(--v2026-sp-4);
}

.v2026-remind__footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: var(--v2026-sp-2);
  margin-top: var(--v2026-sp-4);
}

.v2026-remind__reset {
  width: 40px;
  height: 40px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--v2026-border);
  border-radius: 8px;
  background: var(--v2026-surface);
  color: var(--v2026-icon);
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover {
    background: var(--v2026-surface-hover);
  }
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 3px var(--v2026-cal-focus-ring, rgba(49, 193, 215, 0.30));
  }
}

.v2026-remind__confirm {
  height: 40px;
  padding: 0 var(--v2026-sp-5);
  border: 0;
  border-radius: 8px;
  background: var(--v2026-remind-confirm-bg);
  color: #fff;
  font-family: inherit;
  font-size: var(--v2026-fs-body);
  font-weight: 600;
  cursor: pointer;
  transition: background-color 150ms ease;

  &:hover {
    background: var(--v2026-teal-hover, #159C96);
  }
}

.v2026-field + .v2026-field {
  margin-top: var(--v2026-sp-3);
}

.v2026-field__label {
  display: block;
  margin-bottom: 6px;
  font-size: 12px;
  font-weight: 500;
  color: var(--v2026-text-secondary);
}

.v2026-field__control {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  height: var(--v2026-remind-field-h, 40px);
  padding: 0 var(--v2026-sp-3);
  border: 1px solid var(--v2026-border);
  border-radius: 8px;
  background: var(--v2026-surface);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease;

  &:hover {
    background: var(--v2026-surface-hover);
  }

  &:focus-visible,
  &:focus-within {
    outline: none;
    border-color: #31C1D7;
    box-shadow: 0 0 0 3px var(--v2026-cal-focus-ring, rgba(49, 193, 215, 0.30));
  }
}

.v2026-field__ico {
  width: 18px;
  height: 18px;
  flex-shrink: 0;
  background-color: var(--v2026-icon);
  -webkit-mask-repeat: no-repeat;
          mask-repeat: no-repeat;
  -webkit-mask-position: center;
          mask-position: center;
  -webkit-mask-size: contain;
          mask-size: contain;
}

.v2026-field__value {
  flex: 1 1 auto;
  min-width: 0;
  font-size: var(--v2026-fs-body);
  color: var(--v2026-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2026-field__value--empty {
  color: var(--v2026-text-muted);
}

.v2026-field__input {
  border: 0;
  background: transparent;
  padding: 0;
  outline: none;

  &::placeholder {
    color: var(--v2026-text-muted);
  }
}

.v2026-field__native {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  opacity: 0;
  cursor: pointer;
  -webkit-appearance: none;
          appearance: none;
}

.v2026-field__chev {
  flex-shrink: 0;
  font-size: 10px;
  color: var(--v2026-text-muted);
  transition: transform 150ms ease;
}

.v2026-field__panel {
  margin-top: 6px;
}

.v2026-field__error {
  margin-top: 6px;
  font-size: 12px;
  color: var(--v2026-red);
}

.v2026-field.is-open .v2026-field__control {
  border-color: #31C1D7;
}

.v2026-field.is-open .v2026-field__chev {
  transform: rotate(180deg);
}

.v2026-field.is-error .v2026-field__control {
  border-color: var(--v2026-red);
}

.v2026-field.is-disabled .v2026-field__control {
  background: var(--v2026-surface-sunken);
  color: var(--v2026-text-muted);
  cursor: not-allowed;
  pointer-events: none;
}

.v2026-remind-datepanel ::v-deep .v2026-cal {
  --v2026-cal-pad: 8px;
  --v2026-cal-chevron-size: 24px;
  --v2026-cal-chevron-icon-size: 14px;
  --v2026-cal-num-fs: 12px;
  --v2026-cal-title-fs: 13px;
  --v2026-cal-dot-row-h: 0px;
  --v2026-cal-dot-bottom: 0px;
}

.v2026-field__control--legacy {
  cursor: default;

  ::v-deep .vdp-datepicker {
    flex: 1 1 auto;
    min-width: 0;
  }
  ::v-deep input {
    width: 100%;
    padding: 0;
    border: 0;
    background: transparent;
    outline: none;
    font-size: var(--v2026-fs-body);
    color: var(--v2026-text);

    &::placeholder {
      color: var(--v2026-text-muted);
    }
  }
}

.v2026-field__hit {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  -webkit-appearance: none;
          appearance: none;
}

.v2026-field__clear {
  position: relative;
  z-index: 1;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--v2026-text-muted, #98A2B3);
  font-size: 11px;
  line-height: 1;
  cursor: pointer;
  transition: background-color 150ms ease, color 150ms ease;

  &:hover {
    color: var(--v2026-text, #101828);
    background: var(--v2026-border, #E4E7EC);
  }
  &:active {
    background: #D0D5DD;
  }
  &:focus-visible {
    outline: 2px solid var(--v2026-color-main, #31C1D7);
    outline-offset: 1px;
  }
}

.v2026-field__chevbtn {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 0 0 0 6px;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.v2026-timelist {
  max-height: 200px;
  overflow-y: auto;
  padding: 4px 0;
  border: 1px solid var(--v2026-border);
  border-radius: 8px;
}

.v2026-timelist__row {
  display: block;
  width: 100%;
  height: 32px;
  padding: 0 var(--v2026-sp-3);
  border: 0;
  background: transparent;
  color: var(--v2026-text);
  font-family: inherit;
  font-size: var(--v2026-fs-body);
  text-align: left;
  cursor: pointer;

  &:hover {
    background: var(--v2026-surface-hover);
  }
  &:focus-visible {
    outline: none;
    box-shadow: inset 0 0 0 2px var(--v2026-cal-focus-ring, rgba(49, 193, 215, 0.30));
  }
  &.is-selected {
    color: #31C1D7;
    font-weight: 600;
  }
}

.v2026-timelist__current {
  position: sticky;
  top: 0;
  z-index: 1;
  background: var(--v2026-surface);
}

.v2026-timelist__sep {
  height: 1px;
  margin: 4px 0;
  background: var(--v2026-border);
}
</style>
